import {
  AppCashbackApi,
  AppCashbackDto,
  CashbackPopupDto,
  CashbackPopupsApi,
} from '@coretava/api/ecommerce-service';
import { getLocalStorageItem, setLocalStorageItem } from '@gamiphy/utils/storage';
import maxBy from 'lodash/maxBy';
import minBy from 'lodash/minBy';
import {
  action, computed, makeAutoObservable, observable, runInAction, when,
} from 'mobx';
import { AppStore } from '../app';
import { LevelStore } from '../level';
import { UserStore } from '../user';
import { UTM_CAMPAIGN_LOCAL_STORAGE } from './constants';

class AppCashbackStore {
  @observable initialized = false;

  @observable appCashbacks: AppCashbackDto[] = [];

  @observable cashbackPopup: CashbackPopupDto | null = null;

  private readonly urlParams = new URLSearchParams(window.location.search);

  constructor() {
    makeAutoObservable(this);

    void this.initialize();
  }

  @computed
  get api(): AppCashbackApi {
    return new AppCashbackApi({
      isJsonMime(): boolean {
        return true;
      },
      basePath: process.env.BASE_API_URL,
      accessToken: UserStore.token,
    });
  }

  @computed
  get cashbackPopupsApi(): CashbackPopupsApi {
    return new CashbackPopupsApi({
      isJsonMime(): boolean {
        return true;
      },
      basePath: process.env.BASE_API_URL,
      accessToken: UserStore.token,
    });
  }

  @computed
  get defaultAppCashback(): AppCashbackDto | undefined {
    return this.appCashbacks[0];
  }

  @computed
  get utmCampaign(): string | null {
    return getLocalStorageItem(UTM_CAMPAIGN_LOCAL_STORAGE);
  }

  @computed get shouldShowCashbackPopup(): boolean {
    return this.defaultAppCashback?.useCashbackPopups ? !!this.cashbackPopup : true;
  }

  @computed get currentAppCashback(): AppCashbackDto | undefined {
    if (LevelStore.tierConfig?.enabled && UserStore.isLoggedIn) {
      return this.appCashbacks.find((appCashback) => appCashback.tier === LevelStore.currentTier?.order);
    }
    return this.defaultAppCashback;
  }

  @computed
  get highestPromotionAppCashback(): AppCashbackDto | undefined {
    return LevelStore.tierConfig?.enabled
      ? this.appCashbacks.find((appCashback) => appCashback.tier === LevelStore.currentTier?.order)
      : maxBy(this.appCashbacks, (appCashback) => appCashback.promotion?.value);
  }

  @computed
  get lowestPromotionAppCashback(): AppCashbackDto | undefined {
    return minBy(this.appCashbacks, (appCashback) => appCashback.promotion?.value);
  }

  @action
  async initialize(): Promise<void> {
    const UtmCampaign = this.urlParams.get('utm_campaign');
    setLocalStorageItem(UTM_CAMPAIGN_LOCAL_STORAGE, UtmCampaign ?? '');
    await when(() => AppStore.initialized);

    try {
      const response = await this.api.appCashbackControllerList(AppStore.app.id);
      const useCashbackPopups = response.data.appCashbacks[0]?.useCashbackPopups;

      if (useCashbackPopups) {
        await this.initializeCashbackPopup();
      }

      runInAction(() => {
        this.appCashbacks = response.data.appCashbacks;
      });
    } finally {
      runInAction(() => {
        this.initialized = true;
      });
    }
  }

  @action
  async initializeCashbackPopup(): Promise<void> {
    if (!this.utmCampaign) return;

    const cashbackPopupResponse = await this.cashbackPopupsApi.cashbackPopupControllerGetCashbackPopup(
      AppStore.app.id,
      this.utmCampaign,
    );

    runInAction(() => {
      this.cashbackPopup = cashbackPopupResponse?.data.cashbackPopup || null;
    });
  }
}

export default new AppCashbackStore();
